import { useCallback, useEffect, useRef, useState } from 'react';

export type ToastKind = 'success' | 'error';

export interface IToast {
  id: number;
  kind: ToastKind;
  title: string;
  sub?: string;
}

const TOAST_TTL_MS = 4600;

/** Two toasts say the same thing — the second would only stack a copy. */
const sameToast = (a: Omit<IToast, 'id'>, b: Omit<IToast, 'id'>): boolean =>
  a.kind === b.kind && a.title === b.title && a.sub === b.sub;

/**
 * A toast queue: push a toast, dismiss it by hand. A SUCCESS dismisses itself
 * after ~4.6 s — it confirms what the user just watched happen. An ERROR stays
 * until dismissed: it reports something that did NOT happen, and a message that
 * fades while the user looks elsewhere leaves a failure looking like nothing
 * happened at all. A push that repeats a toast still showing is dropped, so a
 * burst of the same failure reads as one message.
 *
 * The app's shared queue is `ToastProvider` (read with `useToast`); a page may
 * still keep its own.
 */
export function useToasts() {
  const [toasts, setToasts] = useState<IToast[]>([]);
  const seq = useRef(0);
  // Pending auto-dismiss timers keyed by toast id, so a manual dismiss can
  // cancel its timer and unmount can clear all of them — otherwise a timer
  // outliving the component fires setState on a dead tree.
  const timers = useRef(new Map<number, number>());
  // The toasts on screen, readable synchronously by `push` — two pushes in one
  // tick must see each other, which the state alone (batched) does not offer.
  const shown = useRef<IToast[]>([]);

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    shown.current = shown.current.filter((toast) => toast.id !== id);
    setToasts(shown.current);
  }, []);

  const push = useCallback(
    (toast: Omit<IToast, 'id'>) => {
      if (shown.current.some((visible) => sameToast(visible, toast))) return;
      seq.current += 1;
      const id = seq.current;
      shown.current = [...shown.current, { ...toast, id }];
      setToasts(shown.current);
      if (toast.kind === 'error') return;
      const timer = window.setTimeout(() => dismiss(id), TOAST_TTL_MS);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => window.clearTimeout(timer));
      pending.clear();
    };
  }, []);

  return { toasts, push, dismiss };
}
